import { useAtom, useAtomValue } from 'jotai';
import { useSortedCompanies } from 'modules/common';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { activeCompanyIdState, routeParamsState } from 'shared/states';

export const useActiveCompanyId = () => {
    const companies = useSortedCompanies();
    const firstCompanyId = companies[0]?.id;
    const [activeCompanyId, setActiveCompanyId] = useAtom(activeCompanyIdState);
    const findActiveCompanyById = useMemo(
        () => companies.some(({ id }) => id === activeCompanyId),
        [activeCompanyId, companies]
    );

    const { pathname } = useLocation();
    const { companyId: pathCompanyId } = useAtomValue(routeParamsState);

    const rootCompanyIdToSet = useMemo(() => {
        const isRootCompanyId = pathCompanyId && pathname && RegExp(`^/${pathCompanyId}`).test(pathname);
        const companyExists = companies.some(({ id }) => id === pathCompanyId);
        const needToSetCompany = activeCompanyId !== pathCompanyId;

        return isRootCompanyId && companyExists && needToSetCompany ? pathCompanyId : '';
    }, [activeCompanyId, companies, pathCompanyId, pathname]);

    /**
     * This effect ensures we always have a valid company selected by:
     * 1. Prioritizing the company from URL path if it exists and matches a valid company
     * 2. Maintaining current selection if the company still exists in the list
     * 3. Falling back to the first available company
     * 4. Setting empty string if no companies are available
     */
    useEffect(() => {
        if (rootCompanyIdToSet) {
            setActiveCompanyId(rootCompanyIdToSet);

            return;
        }

        if (findActiveCompanyById) return;

        if (firstCompanyId) {
            setActiveCompanyId(firstCompanyId);
        } else {
            setActiveCompanyId('');
        }
    }, [findActiveCompanyById, firstCompanyId, rootCompanyIdToSet, setActiveCompanyId]);
};
